import React, { useMemo } from 'react';

import type { Field } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import {
	useFieldTypeIcon,
	useFieldEmoji,
} from '@jira/polaris__component-fields-store/src/useFieldLabelsMap.tsx';
import { useEmoji } from '@jira/polaris__lib-emoji-picker/src/useEmoji.tsx';
import ShortcutScope from '@jira/platform__common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
// oxlint-disable-next-line no-restricted-imports
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Button from '@atlaskit/button/default/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Emoji } from '@atlaskit/emoji';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

const titleMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.title',
	defaultMessage: 'Save as global view',
	description: 'Title of the modal for saving a view as a global view',
});

const descriptionMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.description',
	defaultMessage:
		'Converting to a global view will save only the global fields from this view. You can later select where to add this global view from the admin settings.',
	description: 'Description shown in the modal for saving a view as a global view',
});

const globalFieldsTitleMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.global-fields-title',
	defaultMessage: 'Global fields will be added',
	description: 'Heading for the list of global fields shown in the modal',
});

const cancelMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.cancel',
	defaultMessage: 'Cancel',
	description: 'Label for the cancel button in the save as global view modal',
});

const saveMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.save',
	defaultMessage: 'Save',
	description: 'Label for the save button in the save as global view modal',
});

const spaceFieldsNoteMessage = defineMessage({
	id: 'polaris-component.clone-view-into-global.modal.space-fields-note',
	defaultMessage:
		"{fieldNames} {count, plural, one {is a space field and} other {are space fields and}} thus can't be added to global views.",
	description: 'Note about space fields that cannot be added to global views',
});

const styles = cssMap({
	globalFieldsBox: {
		backgroundColor: token('color.background.information'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	fieldItem: {
		overflow: 'hidden',
	},
	fieldsGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
		gap: token('space.100'),
	},
});

type FieldIconProps = {
	field: Field;
};

const FieldIcon = ({ field }: FieldIconProps): React.JSX.Element => {
	const icon = useFieldTypeIcon(field.key, { label: field.label, size: 'small' });
	const emojiId = useFieldEmoji(field.key);
	const emoji = useEmoji(emojiId);
	return emoji ? <Emoji emoji={emoji} fitToHeight={16} showTooltip /> : <>{icon}</>;
};

type FieldListItemProps = {
	field: Field;
};

const FieldListItem = ({ field }: FieldListItemProps): React.JSX.Element => (
	<Flex alignItems="center" gap="space.075" xcss={styles.fieldItem}>
		<FieldIcon field={field} />
		<Text size="small">{field.label}</Text>
	</Flex>
);

type CloneViewIntoGlobalModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	fields: Field[];
};

export const CloneViewIntoGlobalModal = ({
	isOpen,
	onClose,
	onConfirm,
	fields,
}: CloneViewIntoGlobalModalProps): React.JSX.Element => {
	const { formatMessage, formatList } = useIntl();

	const globalFields = useMemo(() => fields.filter((f) => f.global), [fields]);
	const spaceFields = useMemo(() => fields.filter((f) => f.custom && !f.global), [fields]);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<ModalDialog onClose={onClose}>
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(titleMessage)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Stack space="space.300">
								<Text>{formatMessage(descriptionMessage)}</Text>
								{globalFields.length > 0 && (
									<Box xcss={styles.globalFieldsBox}>
										<Stack space="space.150">
											<Text weight="bold" size="medium">
												{formatMessage(globalFieldsTitleMessage)}
											</Text>
											<Box xcss={styles.fieldsGrid}>
												{globalFields.map((field) => (
													<FieldListItem key={field.key} field={field} />
												))}
											</Box>
										</Stack>
									</Box>
								)}
								{spaceFields.length > 0 && (
									<Text>
										{formatMessage(spaceFieldsNoteMessage, {
											fieldNames: formatList(spaceFields.map((f) => f.label)),
											count: spaceFields.length,
										})}
									</Text>
								)}
							</Stack>
						</ModalBody>
						<ModalFooter>
							<Button onClick={onClose}>{formatMessage(cancelMessage)}</Button>
							<Button appearance="primary" onClick={onConfirm}>
								{formatMessage(saveMessage)}
							</Button>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};
